import type { PaymentState } from "@/api/custom_types";
import { useTranslation } from "@/hooks";
import { Button, Icon, Typography } from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ComponentProps, FC } from "react";

type IconName = ComponentProps<typeof Icon>["name"];

const STATE_STYLE: Record<
	PaymentState,
	{ icon: IconName; iconClass: string; ringClass: string }
> = {
	pending: {
		icon: "time-line",
		iconClass: "text-primary-100",
		ringClass: "bg-primary-fill/10",
	},
	accepted: {
		icon: "check-double-line",
		iconClass: "text-success-text",
		ringClass: "bg-success-fill/20",
	},
	rejected: {
		icon: "forbid-line",
		iconClass: "text-danger-text",
		ringClass: "bg-danger-fill/20",
	},
};

interface PaymentResultStepProps {
	state: PaymentState;
	onClose: () => void;
	amount?: string;
	user?: string;
	message?: string;
	/** Hidden when this is already shown from the payments list. */
	showPaymentsLink?: boolean;
	/** Which payments tab this payment will show up in. */
	paymentsTab?: "sent" | "received";
}

/**
 * The end of the flow: what was filed, what happens next, and a way to go find
 * it again - a pending payment is something the user will want to come back to.
 */
const PaymentResultStep: FC<PaymentResultStepProps> = ({
	state,
	onClose,
	amount,
	user,
	message,
	showPaymentsLink = true,
	paymentsTab = "sent",
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const style = STATE_STYLE[state];

	const title = {
		pending: t("payment.add.statusDialog.infoTitle"),
		accepted: t("payment.add.statusDialog.confirmedTitle"),
		rejected: t("payment.add.statusDialog.rejectTitle"),
	}[state];

	const body =
		message ??
		{
			pending: t("payment.add.statusDialog.infoMessage"),
			accepted: t("payment.add.statusDialog.confirmedMessage", {
				amount: amount ?? "",
				user: user ?? "",
			}),
			rejected: t("payment.add.statusDialog.rejectMessage"),
		}[state];

	return (
		<>
			<div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-8 text-center">
				<span
					className={`flex size-14 items-center justify-center rounded-full ${style.ringClass}`}
				>
					<Icon name={style.icon} size="lg" className={style.iconClass} />
				</span>
				<Typography variant="title-sm" as="p">
					{title}
				</Typography>
				{amount && user && state !== "accepted" && (
					<Typography variant="body-md" className="tabular-nums">
						{t("payment.form.summary", { amount, user })}
					</Typography>
				)}
				<Typography
					variant="body-sm"
					className="text-balance text-secondary-text-100"
				>
					{body}
				</Typography>
			</div>

			<div className="flex flex-col-reverse gap-2 border-secondary-border border-t px-4 py-4 sm:flex-row sm:justify-end">
				<Button type="button" color="secondary" size="lg" onClick={onClose}>
					{t("common.button.close")}
				</Button>
				{showPaymentsLink && (
					// Not `asChild`: the kit's Button always renders its children as an
					// array (icon slots), and Radix Slot rejects more than one child.
					<Button
						type="button"
						color="primary"
						size="lg"
						className="normal-case"
						onClick={() => {
							onClose();
							navigate({ to: "/payment", search: { tab: paymentsTab } });
						}}
					>
						{t("payment.form.goToPayments")}
					</Button>
				)}
			</div>
		</>
	);
};

export default PaymentResultStep;
